import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { StudioDetailsForm } from './studio-details-form';
import { PlanSelectionForm } from './plan-selection-form';
import { TrialConfirmation } from './trial-confirmation';
import type { CreateTrialSubscriptionRequest } from '../../types/subscription';

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

function StepIndicator({ currentStep, totalSteps }: StepIndicatorProps) {
  return (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: totalSteps }, (_, index) => (
        <React.Fragment key={index}>
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              index + 1 <= currentStep 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {index + 1}
          </div>
          {index < totalSteps - 1 && (
            <div 
              className={`w-12 h-1 mx-2 ${
                index + 1 < currentStep ? 'bg-blue-600' : 'bg-gray-200'
              }`} 
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const STEP_TITLES = [
  'Studio Details',
  'Choose Plan',
  'Start Trial'
];

export function StudioSignupFlow() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<CreateTrialSubscriptionRequest>>({});
  
  const totalSteps = 3;

  const updateFormData = (data: Partial<CreateTrialSubscriptionRequest>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StudioDetailsForm
            initialData={formData}
            onNext={(data) => {
              updateFormData(data);
              nextStep();
            }}
          />
        );
      case 2:
        return (
          <PlanSelectionForm
            initialData={formData}
            onNext={(data) => {
              updateFormData(data);
              nextStep();
            }}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <TrialConfirmation
            formData={formData as CreateTrialSubscriptionRequest}
            onBack={prevStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to CueFlow
          </h1>
          <p className="text-lg text-gray-600">
            Start your 60-day free trial today
          </p>
        </div>

        <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-center">
              {STEP_TITLES[currentStep - 1]}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderStep()}
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-sm text-gray-500">
          <p>Already have an account? <a href="/login" className="text-blue-600 hover:underline">Sign in</a></p>
        </div>
      </div>
    </div>
  );
}